import {
  Path,
  PathValue,
  useController,
  UseControllerProps,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FileWithPath } from 'react-dropzone';
import { ValidationOptions } from '../../../types/form';
import { applyFileFieldValidationOptions } from './applyFileFieldValidationOptions';

export type FileFieldFieldValidationOptions<TFieldValues> =
  ValidationOptions<TFieldValues> & {
    atLeastOneFile?: boolean;
    maxFileSize?: {
      value?: number;
      message?: string;
    };
  };

export interface UseFileFieldControllerProps<TFieldValues>
  extends Omit<UseControllerProps<TFieldValues>, 'control' | 'rules'> {
  rules?: FileFieldFieldValidationOptions<TFieldValues>;
}

export function useFileFieldController<TFieldValues, TSavedFile = FileWithPath>(
  props: UseFileFieldControllerProps<TFieldValues>,
) {
  const { rules = {}, name, ...restControllerProps } = props;

  const { t } = useTranslation();
  const { control, getValues, setValue, trigger } =
    useFormContext<TFieldValues>();

  const fileFieldRules = applyFileFieldValidationOptions<TFieldValues>(
    name,
    getValues,
    t,
    rules,
  );
  const required = fileFieldRules.atLeastOneFile;

  const controllerProps = useController<TFieldValues>({
    name,
    control,
    rules: fileFieldRules,
    ...restControllerProps,
  });

  const files =
    (useWatch<TFieldValues>({
      name,
    }) as Array<FileWithPath | TSavedFile>) || [];

  const handleFileSelect = (file: File) => {
    setValue(name, [...files, file] as PathValue<
      TFieldValues,
      Path<TFieldValues>
    >);

    trigger(name);
  };

  const handleFileRemove = (fileIndex: number) => {
    const filteredFiles = files.filter((_file, index) => index !== fileIndex);

    setValue(
      name,
      filteredFiles as PathValue<TFieldValues, Path<TFieldValues>>,
    );

    trigger(name);
  };

  return {
    files,
    required,
    handleFileSelect,
    handleFileRemove,
    ...controllerProps,
  };
}
